// Planilha Gran (25/09/2026) — Financeiro > Faturas > card Gran > "Planilha Gran".
//
// A Vigor monta aqui, a partir das faturas do sistema, a planilha mensal dos condomínios
// da Gran e manda a versão dela para a Gran (antes a Gran mandava a dela, montada no
// gdash, com UC repetida e total errado). Mesmo jeito da planilha da extração de faturas:
// abas, filtro por situação, grade tipo Excel e exportação.
//
// Os dados vêm das props (faturas e UCs que o quadro de faturas já carregou e mantém
// atualizadas pelo tempo real): a tela não baixa a base de novo. Do banco ela só lê a
// lista de UCs (planilha_gran_itens) e o histórico de envios.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Building2, Download, FileSpreadsheet, History, ListChecks, Loader2, Search, Send, Ticket, X } from 'lucide-react';
import SpreadsheetGrid from '../../Gestao/Extracao/planilha/SpreadsheetGrid';
import ConfirmationModal from '../../../src/components/ConfirmationModal';
import { useToast } from '../../../src/contexts/ToastContext';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logAction, logUsageEvent } from '../../../src/lib/logger';
import {
  ETAPAS, FILTRO_VAZIO, contarEtapas, filtrarLinhas, gridCondominios, gridUcs, linhasParaEnvio,
  mesesDisponiveis, montarLinhasGran, resumirCondominios, resumoConferenciaGran, totalizar,
  type EtapaGran, type FiltroPlanilhaGran, type ItemPlanilhaGran, type FaturaPlanilhaGran, type UcPlanilhaGran,
} from './planilhaGranModel';
import * as api from './planilhaGranApi';
import { baixarConferencia, baixarPlanilhaParaGran } from './exportarPlanilhaGran';
import AbaEmitirGran, { type ItemFilaGran } from './AbaEmitirGran';
import AbaListaGran from './AbaListaGran';
import AbaEnviadasGran from './AbaEnviadasGran';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  empresa: { id: string; nome: string } | null;
  faturas: FaturaPlanilhaGran[];
  ucs: UcPlanilhaGran[];
  /** adm, gerente e analista: mexem na lista, emitem e registram o envio. */
  podeGerir: boolean;
  onAbrirFatura: (faturaId: number) => void;
  onEmitirEmSequencia: (itens: ItemFilaGran[]) => void;
  /** Uma janela do fluxo da fatura está aberta por cima: o Esc é dela, não desta tela. */
  fluxoAberto?: boolean;
}

type Aba = 'condominios' | 'ucs' | 'emitir' | 'lista' | 'enviadas';

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Cor do chip de cada etapa (mesma lógica de cor da extração: verde terminou, âmbar
 *  pede ação, vermelho travou). */
const CLASSE_ETAPA: Record<EtapaGran | 'todas' | 'revisar', string> = {
  todas: 'text-gray-600 border-gray-300 dark:text-gray-300 dark:border-gray-600',
  revisar: 'text-amber-700 border-amber-300 dark:text-amber-300 dark:border-amber-700',
  pronta: 'text-primary border-primary/40 dark:text-lime-300 dark:border-lime-700',
  emitida: 'text-sky-700 border-sky-300 dark:text-sky-300 dark:border-sky-700',
  paga: 'text-emerald-700 border-emerald-300 dark:text-emerald-300 dark:border-emerald-700',
  em_andamento: 'text-gray-600 border-gray-300 dark:text-gray-300 dark:border-gray-600',
  sem_cobranca: 'text-gray-500 border-gray-300 dark:text-gray-400 dark:border-gray-600',
  sem_fatura: 'text-red-600 border-red-300 dark:text-red-300 dark:border-red-700',
  cancelada: 'text-red-600 border-red-300 dark:text-red-300 dark:border-red-700',
};
const ORDEM_CHIPS: (EtapaGran | 'todas' | 'revisar')[] = ['todas', 'revisar', 'pronta', 'emitida', 'paga', 'em_andamento', 'sem_cobranca', 'sem_fatura', 'cancelada'];
const rotuloChip = (k: EtapaGran | 'todas' | 'revisar') =>
  k === 'todas' ? 'Todas' : k === 'revisar' ? 'A conferir' : ETAPAS.find(e => e.key === k)?.rotulo ?? k;

const PlanilhaGranModal: React.FC<Props> = ({ isOpen, onClose, empresa, faturas, ucs, podeGerir, onAbrirFatura, onEmitirEmSequencia, fluxoAberto = false }) => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [aba, setAba] = useState<Aba>('condominios');
  const [itens, setItens] = useState<ItemPlanilhaGran[] | null>(null);
  const [erroItens, setErroItens] = useState('');
  const [mes, setMes] = useState('');
  const [filtro, setFiltro] = useState<FiltroPlanilhaGran>(FILTRO_VAZIO);
  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [versaoEnvios, setVersaoEnvios] = useState(0);

  const carregarItens = useCallback(async () => {
    if (!empresa) return;
    setErroItens('');
    try {
      setItens(await api.listarItens(empresa.id));
    } catch (e: any) {
      setErroItens(e.message);
      setItens([]);
    }
  }, [empresa]);

  useEffect(() => {
    if (!isOpen) return;
    carregarItens();
    logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'planilha_gran', acao: 'abrir', origem: 'acao' });
  }, [isOpen, carregarItens]);

  // Esc fecha — só quando não há um modal do fluxo por cima (eles tratam o próprio Esc).
  useEffect(() => {
    if (!isOpen || fluxoAberto) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !confirmarEnvio) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, confirmarEnvio, fluxoAberto]);

  const meses = useMemo(() => (itens ? mesesDisponiveis(itens, ucs, faturas) : []), [itens, ucs, faturas]);
  const mesAtivo = mes || meses[0] || '';

  const linhasMes = useMemo(
    () => (itens && empresa && mesAtivo ? montarLinhasGran({ itens, ucs, faturas, mes: mesAtivo, granId: empresa.id }) : []),
    [itens, ucs, faturas, mesAtivo, empresa],
  );
  // Recorte sem a etapa: é sobre ele que os chips contam (o número do chip é o que
  // aparece ao clicar nele).
  const recorte = useMemo(() => filtrarLinhas(linhasMes, { ...filtro, etapa: 'todas' }), [linhasMes, filtro]);
  const filtradas = useMemo(() => filtrarLinhas(recorte, { ...FILTRO_VAZIO, etapa: filtro.etapa }), [recorte, filtro.etapa]);
  const contagem = useMemo(() => contarEtapas(recorte), [recorte]);
  const totais = useMemo(() => totalizar(filtradas), [filtradas]);
  const resumo = useMemo(() => resumirCondominios(filtradas), [filtradas]);
  const gUcs = useMemo(() => gridUcs(filtradas), [filtradas]);
  const gCond = useMemo(() => gridCondominios(resumo), [resumo]);
  const grupos = useMemo(() => Array.from(new Set(linhasMes.map(l => l.grupo))).sort((a, b) => a.localeCompare(b, 'pt-BR')), [linhasMes]);
  const temFiltro = filtro.etapa !== 'todas' || !!filtro.grupo || filtro.origem !== 'todas' || !!filtro.busca.trim();

  // #6116: quando e de onde veio a conferência da Gran — só uma legenda curta (ponto do
  // systems-thinker), não uma coluna nova.
  const conferenciaGran = useMemo(() => (itens ? resumoConferenciaGran(itens) : null), [itens]);
  const dataConferenciaGranBr = conferenciaGran
    ? new Date(`${conferenciaGran.data}T00:00:00`).toLocaleDateString('pt-BR')
    : null;

  const descricaoFiltro = () => {
    const partes: string[] = [];
    if (filtro.grupo) partes.push(`Condomínio: ${filtro.grupo}`);
    if (filtro.etapa !== 'todas') partes.push(`Situação: ${rotuloChip(filtro.etapa)}`);
    if (filtro.origem !== 'todas') partes.push(filtro.origem === 'extra' ? 'Só extras' : 'Só do modelo');
    if (filtro.busca.trim()) partes.push(`Busca: "${filtro.busca.trim()}"`);
    return partes.join(' · ') || null;
  };

  const baixarParaGran = async () => {
    setConfirmarEnvio(false);
    if (!empresa || filtradas.length === 0) return;
    const linhas = linhasParaEnvio(filtradas);
    baixarPlanilhaParaGran(linhas, mesAtivo);
    if (!podeGerir) {
      addToast('Planilha baixada.', 'success');
      return;
    }
    try {
      await api.registrarEnvio(empresa.id, mesAtivo, linhas, descricaoFiltro());
      setVersaoEnvios(v => v + 1);
      await logAction(user?.id || 'SYSTEM', 'Gerou planilha Gran', { mes: mesAtivo, linhas: linhas.length, filtro: descricaoFiltro() });
      addToast('Planilha baixada e guardada em "Geradas".', 'success');
    } catch (e: any) {
      addToast(e.message, 'warning', 8000);
    }
  };

  const pedirEnvio = () => {
    if (filtradas.length === 0) return;
    if (totais.semValor > 0 || filtradas.some(l => l.motivos.length) || temFiltro) setConfirmarEnvio(true);
    else baixarParaGran();
  };

  // Colunas clicáveis da grade de UCs, achadas pelo nome (a ordem muda quando entra coluna nova).
  const COL_UC = gUcs.columns.findIndex(col => col.label === 'UC');
  const COL_FATURA = gUcs.columns.findIndex(col => col.label === 'Fatura nº');
  const COL_LINK = gUcs.columns.findIndex(col => col.label === 'LINK DO BOLETO');
  const acaoUcs = (r: number, c: number) => {
    const l = filtradas[r];
    if (!l) return null;
    if ((c === COL_UC || c === COL_FATURA) && l.faturaId !== null) return () => onAbrirFatura(l.faturaId as number);
    if (c === COL_LINK && l.link) return () => window.open(l.link as string, '_blank', 'noopener');
    return null;
  };
  const acaoCondominios = (r: number, c: number) => {
    const g = resumo[r];
    if (!g || c !== 0) return null;
    return () => { setFiltro(f => ({ ...f, grupo: g.grupo })); setAba('ucs'); };
  };

  if (!isOpen || !empresa) return null;

  const ABAS: { key: Aba; label: string; icone: React.ReactNode; qtd?: number; visivel: boolean }[] = [
    { key: 'condominios', label: 'Condomínios', icone: <Building2 className="w-3.5 h-3.5" />, qtd: resumo.length, visivel: true },
    { key: 'ucs', label: 'UCs', icone: <FileSpreadsheet className="w-3.5 h-3.5" />, qtd: filtradas.length, visivel: true },
    { key: 'emitir', label: 'Emitir', icone: <Ticket className="w-3.5 h-3.5" />, qtd: recorte.filter(l => l.etapa === 'pronta').length, visivel: podeGerir },
    { key: 'lista', label: 'Lista de UCs', icone: <ListChecks className="w-3.5 h-3.5" />, qtd: itens?.length ?? 0, visivel: true },
    { key: 'enviadas', label: 'Geradas', icone: <History className="w-3.5 h-3.5" />, visivel: true },
  ];
  const mostraFiltros = aba === 'condominios' || aba === 'ucs' || aba === 'emitir';

  return createPortal(
    // z-50 de propósito (e não 9999 como a da extração): as janelas do fluxo da fatura
    // (validar, calcular, emitir) também são z-50 e abrem POR CIMA desta, porque o portal
    // delas entra depois no body. Assim "abrir fatura" e a emissão em sequência mostram a
    // janela de sempre sem fechar a planilha.
    <div className="fixed inset-0 z-50 flex items-stretch justify-center p-2 sm:p-4 animate-fade-in" role="dialog" aria-modal="true" aria-labelledby="planilha-gran-titulo">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full h-full flex flex-col overflow-hidden">
        {/* Cabeçalho */}
        <div className="px-5 py-3 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-white/15 rounded-xl flex items-center justify-center border border-white/20 shrink-0">
              <FileSpreadsheet className="w-5 h-5 text-white" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 id="planilha-gran-titulo" className="text-base font-bold text-white truncate">Planilha {empresa.nome}</h2>
              <p className="text-orange-50/90 text-xs truncate">Condomínios e extras da {empresa.nome} · montada das faturas do sistema</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="planilha-gran-mes" className="sr-only">Mês de referência</label>
            <select
              id="planilha-gran-mes"
              value={mesAtivo}
              onChange={e => setMes(e.target.value)}
              className="text-[13px] font-semibold bg-white/15 text-white border border-white/25 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-white/60 outline-none [&>option]:text-gray-900"
            >
              {meses.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <button onClick={onClose} title="Fechar (Esc)" aria-label="Fechar planilha" className="w-8 h-8 bg-white/15 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/25 transition-all shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {itens === null ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" aria-hidden="true" /> Carregando a planilha…</div>
        ) : erroItens ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 p-8 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-500" aria-hidden="true" />
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{erroItens}</p>
            <button onClick={carregarItens} className="text-[12px] font-semibold text-primary hover:underline">Tentar de novo</button>
          </div>
        ) : (
          <>
            {/* Totais do recorte */}
            <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 shrink-0">
              {[
                { rotulo: 'UCs no recorte', valor: String(totais.ucs), dica: `${grupos.length} condomínios no mês` },
                { rotulo: 'Boleto Gran', valor: brl(totais.boletoGran), dica: totais.semValor ? `${totais.semValor} UC${totais.semValor !== 1 ? 's' : ''} sem valor fora da soma` : 'Soma de todas as UCs' },
                { rotulo: 'Equatorial', valor: brl(totais.equatorial), dica: 'Conta da distribuidora dentro do boleto' },
                { rotulo: 'Comissão (5%)', valor: brl(totais.comissao), dica: '(Boleto − Equatorial) × 5%' },
                {
                  rotulo: 'A conferir', valor: String(contagem.revisar), alerta: contagem.revisar > 0,
                  dica: dataConferenciaGranBr
                    ? `Inclui a conferência da Gran de ${dataConferenciaGranBr}${conferenciaGran?.fonte ? ` (${conferenciaGran.fonte})` : ''}`
                    : 'Linhas com algum motivo de conferência',
                },
              ].map(k => (
                <div key={k.rotulo} className={`rounded-xl px-3 py-2 border ${k.alerta ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/15' : 'border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/40'}`}>
                  <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{k.rotulo}</p>
                  <p className={`text-[15px] font-bold tabular-nums ${k.alerta ? 'text-amber-700 dark:text-amber-300' : 'text-gray-900 dark:text-white'}`}>{k.valor}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate" title={k.dica}>{k.dica}</p>
                </div>
              ))}
            </div>

            {/* Abas + ações */}
            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-2 shrink-0">
              <div className="flex items-center gap-1 flex-wrap" role="tablist" aria-label="Visões da planilha">
                {ABAS.filter(a => a.visivel).map(a => (
                  <button
                    key={a.key}
                    role="tab"
                    aria-selected={aba === a.key}
                    onClick={() => setAba(a.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg transition-colors ${aba === a.key ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}
                  >
                    {a.icone} {a.label}{a.qtd !== undefined && <span className="opacity-70">({a.qtd})</span>}
                  </button>
                ))}
              </div>
              <div className="flex-1" />
              <button
                onClick={() => baixarConferencia(gUcs, gCond, mesAtivo)}
                disabled={filtradas.length === 0}
                title="Excel interno com tudo o que a tela mostra, inclusive o motivo de conferência"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
              >
                <Download className="w-3.5 h-3.5" aria-hidden="true" /> Conferência (Excel)
              </button>
              <button
                onClick={pedirEnvio}
                disabled={filtradas.length === 0}
                title={podeGerir ? 'Baixa a planilha no formato da Gran e guarda uma cópia em "Geradas"' : 'Baixa a planilha no formato da Gran'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-40"
              >
                <Send className="w-3.5 h-3.5" aria-hidden="true" /> Planilha para a {empresa.nome} ({filtradas.length})
              </button>
            </div>

            {/* Filtros */}
            {mostraFiltros && (
              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-2 shrink-0">
                <div className="flex items-center gap-1 flex-wrap" role="group" aria-label="Filtrar por situação">
                  {ORDEM_CHIPS.map(k => {
                    if (k !== 'todas' && k !== filtro.etapa && contagem[k] === 0) return null;
                    const ativo = filtro.etapa === k;
                    return (
                      <button
                        key={k}
                        onClick={() => setFiltro(f => ({ ...f, etapa: k }))}
                        aria-pressed={ativo}
                        title={k === 'todas' || k === 'revisar' ? undefined : ETAPAS.find(e => e.key === k)?.descricao}
                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${ativo ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 border-gray-900 dark:border-gray-100' : `bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 ${CLASSE_ETAPA[k]}`}`}
                      >
                        {rotuloChip(k)} ({contagem[k]})
                      </button>
                    );
                  })}
                </div>
                <div className="flex-1" />
                <label htmlFor="planilha-gran-grupo" className="sr-only">Condomínio</label>
                <select
                  id="planilha-gran-grupo"
                  value={filtro.grupo}
                  onChange={e => setFiltro(f => ({ ...f, grupo: e.target.value }))}
                  className="text-[12px] max-w-[16rem] bg-white border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                >
                  <option value="">Todos os condomínios</option>
                  {grupos.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <label htmlFor="planilha-gran-origem" className="sr-only">Origem</label>
                <select
                  id="planilha-gran-origem"
                  value={filtro.origem}
                  onChange={e => setFiltro(f => ({ ...f, origem: e.target.value as FiltroPlanilhaGran['origem'] }))}
                  className="text-[12px] bg-white border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                >
                  <option value="todas">Modelo + extras</option>
                  <option value="planilha">Só do modelo</option>
                  <option value="extra">Só extras</option>
                </select>
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2" aria-hidden="true" />
                  <input
                    value={filtro.busca}
                    onChange={e => setFiltro(f => ({ ...f, busca: e.target.value }))}
                    placeholder="Nome, UC ou nº da fatura"
                    aria-label="Buscar por nome, UC ou número da fatura"
                    className="w-56 text-[12px] bg-white border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 dark:bg-gray-800 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                  />
                </div>
                {temFiltro && (
                  <button onClick={() => setFiltro(FILTRO_VAZIO)} className="text-[12px] font-medium text-gray-500 hover:text-primary">Limpar</button>
                )}
              </div>
            )}

            {/* Conteúdo */}
            <div className="flex-1 min-h-0">
              {itens.length === 0 && aba !== 'lista' && aba !== 'enviadas' ? (
                <div className="h-full flex flex-col items-center justify-center gap-2 p-8 text-center">
                  <ListChecks className="w-8 h-8 text-gray-300 dark:text-gray-600" aria-hidden="true" />
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">A lista de UCs da planilha está vazia</p>
                  <button onClick={() => setAba('lista')} className="text-[12px] font-semibold text-primary hover:underline">Montar a lista</button>
                </div>
              ) : aba === 'condominios' || aba === 'ucs' ? (
                filtradas.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center gap-2 p-8 text-center">
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Nada neste recorte</p>
                    <button onClick={() => setFiltro(FILTRO_VAZIO)} className="text-[12px] font-semibold text-primary hover:underline">Limpar filtros</button>
                  </div>
                ) : (
                  <div className="h-full p-3">
                    {aba === 'condominios' ? (
                      <SpreadsheetGrid
                        key="condominios"
                        columns={gCond.columns}
                        rows={gCond.rows}
                        editable={false}
                        edited={new Set()}
                        onCellChange={() => {}}
                        acaoCelula={acaoCondominios}
                        dicaAcao="Clique para ver as UCs deste condomínio"
                        freezeCount={1}
                        freezeWidths={[380]}
                      />
                    ) : (
                      <SpreadsheetGrid
                        key="ucs"
                        columns={gUcs.columns}
                        rows={gUcs.rows}
                        editable={false}
                        edited={new Set()}
                        onCellChange={() => {}}
                        acaoCelula={acaoUcs}
                        dicaAcao="Clique para abrir a fatura (na UC ou no nº) ou o boleto (no link)"
                        freezeCount={3}
                        freezeWidths={[220, 240, 150]}
                        statusRow={r => {
                          const l = filtradas[r];
                          if (!l) return undefined;
                          if (l.motivos.length) return 'revisar';
                          if (l.etapa === 'sem_fatura' || l.etapa === 'cancelada') return 'falhou';
                          return l.etapa === 'paga' || l.etapa === 'emitida' ? 'ok' : undefined;
                        }}
                        highlightCol={gUcs.highlightCol}
                      />
                    )}
                  </div>
                )
              ) : aba === 'emitir' ? (
                <AbaEmitirGran linhas={recorte} podeEmitir={podeGerir} onEmitir={onEmitirEmSequencia} onAbrirFatura={onAbrirFatura} />
              ) : aba === 'lista' ? (
                <AbaListaGran empresaId={empresa.id} itens={itens} ucs={ucs} podeGerir={podeGerir} onMudou={carregarItens} />
              ) : (
                <AbaEnviadasGran empresaId={empresa.id} versao={versaoEnvios} />
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmarEnvio}
        onClose={() => setConfirmarEnvio(false)}
        onConfirm={baixarParaGran}
        title={`Baixar a planilha de ${mesAtivo} para a ${empresa.nome}?`}
        description={
          <span className="block space-y-1.5 text-left">
            <span className="block">Vão {filtradas.length} UC{filtradas.length !== 1 ? 's' : ''}, somando {brl(totais.boletoGran)} em boletos.</span>
            {temFiltro && <span className="block">Só o recorte da tela: {descricaoFiltro()}.</span>}
            {totais.semValor > 0 && <span className="block">{totais.semValor} UC{totais.semValor !== 1 ? 's' : ''} ainda sem valor de boleto: na planilha aparece o motivo no lugar do valor.</span>}
            {filtradas.some(l => l.motivos.length) && <span className="block">{filtradas.filter(l => l.motivos.length).length} linha(s) pedem conferência (filtro "A conferir").</span>}
            {podeGerir && <span className="block">Uma cópia fica guardada em "Geradas". Nada é enviado à {empresa.nome} por aqui: você manda o arquivo.</span>}
          </span>
        }
        confirmText="Baixar planilha"
        variant="warning"
      />
    </div>,
    document.body,
  );
};

export default PlanilhaGranModal;
